import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { spxService, SpxSignal, SpxCandidate, DteBucket, AiBestTrade, ModelBestTrade, BucketBest, SpxSkew } from '../../services/spxService';
import { canAccessSymbol } from '../../lib/entitlements';
import '../Dashboard.css';

// ─── Formatting helpers ──────────────────────────────────────────────────────

const fmt = (n: number | undefined | null, d: number = 2): string => {
  if (n === undefined || n === null) return '—';
  return Number(n).toFixed(d);
};

const fmtDate = (iso: string): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const fmtExpiration = (exp: string): string => {
  if (!exp) return '—';
  try {
    const d = new Date(exp + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return exp; }
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const colors = {
  bg: '#0f172a',
  card: '#1f2937',
  cardAlt: '#1a2332',
  border: '#374151',
  text: '#f9fafb',
  textMuted: '#9ca3af',
  textDim: '#6b7280',
  green: '#16a34a',
  greenBg: '#16a34a20',
  blue: '#3b82f6',
  blueBg: '#3b82f620',
  amber: '#d97706',
  amberBg: '#d9770620',
  red: '#dc2626',
};

const containerStyle: React.CSSProperties = {
  maxWidth: 1100, margin: '0 auto', padding: '1.5rem',
  color: colors.text, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const cardStyle: React.CSSProperties = {
  background: colors.card, borderRadius: 8, border: `1px solid ${colors.border}`,
  padding: '1.25rem', marginBottom: '1rem',
};

const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem',
};

const thStyle: React.CSSProperties = {
  padding: '0.5rem 0.4rem', textAlign: 'left' as const, borderBottom: `1px solid ${colors.border}`,
  color: colors.textMuted, fontWeight: 600, fontSize: '0.72rem', whiteSpace: 'nowrap' as const,
};

const tdStyle: React.CSSProperties = {
  padding: '0.45rem 0.4rem', borderBottom: `1px solid ${colors.border}22`,
  whiteSpace: 'nowrap' as const,
};

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: '0.6rem 1.25rem', cursor: 'pointer', border: 'none',
  borderBottom: active ? `2px solid ${colors.blue}` : '2px solid transparent',
  background: 'transparent', color: active ? colors.text : colors.textMuted,
  fontWeight: active ? 600 : 400, fontSize: '0.9rem',
});

const badgeStyle = (color: string, bg: string): React.CSSProperties => ({
  display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: 12,
  fontSize: '0.7rem', fontWeight: 600, background: bg, color,
});

const btnStyle: React.CSSProperties = {
  padding: '0.5rem 1rem', borderRadius: 6, border: 'none', cursor: 'pointer',
  fontWeight: 600, fontSize: '0.85rem', background: colors.blue, color: '#fff',
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const BucketTable: React.FC<{ bucket: DteBucket; bucketKey: string; defaultOpen?: boolean }> = ({ bucket, bucketKey, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={cardStyle}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' as const }}
        onClick={() => setOpen(!open)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.8rem', color: colors.textMuted, transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>&#9654;</span>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>
            {bucket.label}
          </h3>
        </div>
        <span style={badgeStyle(colors.textMuted, colors.border)}>
          {bucket.count} candidates evaluated
        </span>
      </div>
      {bucket.best && <BucketBestBar best={bucket.best} label={bucket.label} />}
      {open && (
        <div style={{ overflowX: 'auto', marginTop: '0.75rem' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {[
                  { label: 'Rank', tip: 'Position in ranking — #1 is best in this group' },
                  { label: 'Expires', tip: 'The date this option contract expires' },
                  { label: 'Strike', tip: 'The price level where this option activates — lower means more safety cushion' },
                  { label: 'Safety Buffer', tip: 'How far below the current price — higher is safer' },
                  { label: 'You Collect', tip: 'The premium you receive upfront for selling this option' },
                  { label: 'Daily Income', tip: 'How much income this trade earns per day until expiration' },
                  { label: 'Risk Level', tip: 'Probability the option finishes in-the-money (lower = less risk)' },
                  { label: 'Risk Group', tip: '5-10 (very safe), 10-20 (sweet spot), 20-30 (moderate), 30-35 (higher risk)' },
                  { label: 'Time Decay', tip: 'How much value this option loses each day — higher is better for sellers' },
                  { label: 'Vol. Sensitivity', tip: 'How much the price changes when market volatility shifts' },
                  { label: 'Volatility', tip: 'How much the market expects the price to swing' },
                  { label: 'Win Chance', tip: 'Estimated probability this trade finishes profitable' },
                  { label: 'Reward/Risk', tip: 'Risk-adjusted return — higher means better return per unit of risk' },
                  { label: 'Overall Score', tip: 'Our composite score weighing safety, income, and efficiency' },
                  { label: 'Alt. Score', tip: 'Alternative scoring model emphasizing income and safety equally' },
                ].map(h => (
                  <th key={h.label} style={thStyle} title={h.tip}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bucket.candidates.map((c: SpxCandidate, i: number) => (
                <tr key={`${bucketKey}-${i}`} style={{
                  background: i === 0 ? colors.greenBg : i % 2 === 0 ? colors.cardAlt : 'transparent',
                }}>
                  <td style={{ ...tdStyle, color: i === 0 ? colors.green : colors.text, fontWeight: i === 0 ? 700 : 400 }}>
                    {c.rank_enhanced}
                  </td>
                  <td style={tdStyle}>{fmtExpiration(c.expiration)}</td>
                  <td style={tdStyle}>{c.strike}</td>
                  <td style={tdStyle}>{fmt(c.points_from_atm, 1)}</td>
                  <td style={tdStyle}>${fmt(c.credit)}</td>
                  <td style={{ ...tdStyle, color: colors.green }}>${fmt(c.credit_per_day, 3)}</td>
                  <td style={tdStyle}>{fmt(c.delta, 3)}</td>
                  <td style={tdStyle}>
                    <span style={badgeStyle(colors.text, colors.border)}>{c.delta_bucket}</span>
                  </td>
                  <td style={tdStyle}>{fmt(Math.abs(c.theta), 1)}</td>
                  <td style={tdStyle}>{fmt(Math.abs(c.vega), 1)}</td>
                  <td style={tdStyle}>{fmt(c.iv_pct, 1)}%</td>
                  <td style={{ ...tdStyle, color: c.prob_profit_pct >= 90 ? colors.green : colors.text }}>
                    {fmt(c.prob_profit_pct, 1)}%
                  </td>
                  <td style={tdStyle}>{fmt(c.sharpe_annualized)}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{fmt(c.score_enhanced, 3)}</td>
                  <td style={tdStyle}>{fmt(c.score_balanced, 3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const bestTradeMetrics = (t: any) => [
  { label: 'Strike Price', value: String(t.strike) },
  { label: 'Expires On', value: fmtExpiration(t.expiration) },
  { label: 'Days Left', value: String(t.dte) },
  { label: 'You Collect', value: `$${fmt(t.credit)}` },
  { label: 'Daily Income', value: t.credit_per_day != null ? `$${fmt(t.credit_per_day, 3)}` : '—' },
  { label: 'Risk Level', value: fmt(t.delta, 4) },
  { label: 'Safety Buffer', value: t.points_from_atm != null ? fmt(t.points_from_atm, 1) : '—' },
  { label: 'Win Chance', value: `${fmt(t.prob_profit_pct, 1)}%` },
  { label: 'Reward/Risk', value: fmt(t.sharpe_annualized) },
  { label: 'Overall Score', value: fmt(t.score_enhanced, 3) },
  { label: 'Volatility', value: t.iv_pct != null ? `${fmt(t.iv_pct, 1)}%` : '—' },
  { label: 'Risk Group', value: t.delta_bucket || '—' },
];

const ModelBestTradeCard: React.FC<{ bestTrade: ModelBestTrade }> = ({ bestTrade }) => (
  <div style={{
    ...cardStyle, borderColor: colors.green, borderWidth: 2,
    background: `linear-gradient(135deg, ${colors.card} 0%, ${colors.green}10 100%)`,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
      <span style={{ fontSize: '1.5rem' }}>&#9733;</span>
      <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Top Recommended Trade</h3>
      <span style={badgeStyle(colors.green, colors.greenBg)}>OUR TOP PICK</span>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
      {bestTradeMetrics(bestTrade).map(s => (
        <div key={s.label} style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.65rem', color: colors.textMuted, marginBottom: '0.15rem' }}>{s.label}</div>
          <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>{s.value}</div>
        </div>
      ))}
    </div>
    <div style={{
      background: colors.cardAlt, borderRadius: 6, padding: '0.75rem',
      border: `1px solid ${colors.border}`,
    }}>
      <div style={{ fontSize: '0.7rem', color: colors.textMuted, marginBottom: '0.25rem', fontWeight: 600 }}>
        Why We Recommend This
      </div>
      <p style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.6 }}>
        This is our top pick because it offers strong income ({fmt(bestTrade.prob_profit_pct, 1)}% estimated chance of profit)
        with a comfortable safety margin
        {bestTrade.points_from_atm > 0 ? ` — the strike price sits ${fmt(bestTrade.points_from_atm, 0)} points below the current market price` : ''}.
        {' '}You would collect ${fmt(bestTrade.credit_per_day, 3)} per day in premium, and the option expires in {bestTrade.dte} days.
        It scored highest in our analysis across safety, income, and efficiency.
      </p>
      <p style={{ margin: '0.5rem 0 0', fontSize: '0.78rem', lineHeight: 1.5, color: colors.textMuted, fontStyle: 'italic' }}>
        Note: We compare options that expire around the same time against each other to find the best in each group.
        The AI looks across all timeframes at once, so it may prefer a different expiration date.
        Both methods are valuable — together they give you a more complete picture.
      </p>
    </div>
  </div>
);

const BucketBestBar: React.FC<{ best: BucketBest; label: string }> = ({ best, label }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
    background: colors.greenBg, borderRadius: 6, padding: '0.5rem 0.75rem',
    border: `1px solid ${colors.green}30`, marginBottom: '0.5rem',
  }}>
    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: colors.green }}>BEST {label}:</span>
    {[
      { label: 'Strike', value: String(best.strike) },
      { label: 'Expires', value: fmtExpiration(best.expiration) },
      { label: 'You Collect', value: `$${fmt(best.credit)}` },
      { label: 'Daily Income', value: `$${fmt(best.credit_per_day, 3)}` },
      { label: 'Safety Buffer', value: fmt(best.points_from_atm, 1) },
      { label: 'Win Chance', value: `${fmt(best.prob_profit_pct, 1)}%` },
      { label: 'Reward/Risk', value: fmt(best.sharpe_annualized) },
      { label: 'Overall Score', value: fmt(best.score_enhanced, 3) },
    ].map(s => (
      <span key={s.label} style={{ fontSize: '0.72rem' }}>
        <span style={{ color: colors.textMuted }}>{s.label} </span>
        <span style={{ fontWeight: 600 }}>{s.value}</span>
      </span>
    ))}
  </div>
);

const skewRegimeConfig: Record<string, { color: string; bg: string; label: string; desc: string }> = {
  normal:   { color: colors.green, bg: colors.greenBg, label: 'CALM',      desc: 'The market is calm \u2014 investors aren\u2019t paying extra for downside protection. This means the options you sell are priced fairly, and conditions are favorable for collecting steady income.' },
  cautious: { color: colors.amber, bg: colors.amberBg, label: 'CAUTIOUS',  desc: 'Investors are starting to buy more protection against a market drop, which actually increases the premiums you collect. However, it also signals some uncertainty ahead \u2014 a good time to stick with safer strikes further from the current price.' },
  elevated: { color: '#f97316',    bg: '#f9731620',    label: 'ELEVATED',  desc: 'There\u2019s significant demand for crash protection \u2014 investors are worried about a sharp decline. While this means higher premiums (more income for you), it also means the market sees real downside risk. Our scores are adjusted to favor more conservative trades. Consider selling fewer contracts or choosing strikes with a wider safety buffer.' },
  extreme:  { color: colors.red,   bg: '#dc262620',    label: 'EXTREME',   desc: 'The market is pricing in a major event \u2014 investors are paying heavily for protection. Premiums are very high, which looks attractive, but the risk of a large move is equally high. Our scores are heavily adjusted to reflect this. This is a time for extreme caution \u2014 consider waiting for conditions to calm before entering new positions.' },
};

const SkewCard: React.FC<{ skew: SpxSkew }> = ({ skew }) => {
  const cfg = skewRegimeConfig[skew.skew_regime] || skewRegimeConfig.normal;
  const expirations = skew.skew_by_expiration ? Object.entries(skew.skew_by_expiration) : [];
  const [showDetail, setShowDetail] = useState(false);

  return (
    <div style={{
      ...cardStyle, borderColor: cfg.color, borderWidth: 2,
      background: `linear-gradient(135deg, ${colors.card} 0%, ${cfg.bg} 100%)`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>Market Fear Gauge</h3>
          <span style={badgeStyle(cfg.color, cfg.bg)}>{cfg.label}</span>
        </div>
        <span style={{ fontSize: '0.75rem', color: colors.textMuted }}>
          {skew.expirations_measured} expirations measured
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.65rem', color: colors.textMuted }}>Fear Index</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: cfg.color }}>{skew.skew_ratio.toFixed(4)}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.65rem', color: colors.textMuted }}>Market Mood</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{skew.skew_regime.charAt(0).toUpperCase() + skew.skew_regime.slice(1)}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.65rem', color: colors.textMuted }}>Score Adjustment</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: skew.skew_penalty < 0 ? colors.red : colors.green }}>
            {skew.skew_penalty === 0 ? 'No change' : `Reduced by ${Math.abs(skew.skew_penalty).toFixed(2)}`}
          </div>
        </div>
      </div>

      <p style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', color: colors.textMuted, lineHeight: 1.5 }}>{cfg.desc}</p>
      <p style={{ margin: 0, fontSize: '0.72rem', color: colors.textDim, fontStyle: 'italic' }}>
        This measures how much more investors are paying to protect against a market drop versus betting on a rise. Normal is 1.05-1.20. Higher values signal growing fear in the market.
      </p>

      {expirations.length > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          <button
            onClick={() => setShowDetail(!showDetail)}
            style={{ background: 'transparent', border: 'none', color: colors.blue, cursor: 'pointer', fontSize: '0.78rem', padding: 0 }}
          >
            {showDetail ? 'Hide' : 'Show'} per-expiration breakdown
          </button>
          {showDetail && (
            <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    {['Expiration', 'Put IV%', 'Call IV%', 'Skew Ratio', 'Put Strike', 'Call Strike'].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {expirations.sort((a, b) => a[0].localeCompare(b[0])).map(([exp, data]) => (
                    <tr key={exp}>
                      <td style={tdStyle}>{fmtExpiration(exp)}</td>
                      <td style={tdStyle}>{data.put_iv.toFixed(1)}%</td>
                      <td style={tdStyle}>{data.call_iv.toFixed(1)}%</td>
                      <td style={{ ...tdStyle, fontWeight: 600, color: data.skew_ratio >= 1.25 ? colors.amber : colors.text }}>
                        {data.skew_ratio.toFixed(4)}
                      </td>
                      <td style={tdStyle}>{data.put_strike}</td>
                      <td style={tdStyle}>{data.call_strike}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BestTradeCard: React.FC<{ bestTrade: AiBestTrade; provider: string; providerColor: string }> = ({ bestTrade, provider, providerColor }) => (
  <div style={{
    ...cardStyle, borderColor: providerColor, borderWidth: 2,
    background: `linear-gradient(135deg, ${colors.card} 0%, ${providerColor}10 100%)`,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
      <span style={{ fontSize: '1.5rem' }}>&#9733;</span>
      <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{provider} AI's Top Pick</h3>
      <span style={badgeStyle(providerColor, `${providerColor}20`)}>AI PICK</span>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
      {bestTradeMetrics(bestTrade).map(s => (
        <div key={s.label} style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.65rem', color: colors.textMuted, marginBottom: '0.15rem' }}>{s.label}</div>
          <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>{s.value}</div>
        </div>
      ))}
    </div>
    <div style={{
      background: colors.cardAlt, borderRadius: 6, padding: '0.75rem',
      border: `1px solid ${colors.border}`,
    }}>
      <div style={{ fontSize: '0.7rem', color: colors.textMuted, marginBottom: '0.25rem', fontWeight: 600 }}>
        Why AI Chose This
      </div>
      <p style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.6 }}>{bestTrade.reasoning}</p>
    </div>
  </div>
);

const AiAnalysisCard: React.FC<{ signal: SpxSignal; onDownload: () => void; downloading: boolean }> = ({ signal, onDownload, downloading }) => {
  const analysis = signal.claude_analysis;

  if (!analysis) {
    return (
      <div style={{ ...cardStyle, borderColor: colors.textDim }}>
        <h3 style={{ margin: '0 0 0.5rem', color: colors.textMuted }}>AI Analysis</h3>
        <p style={{ color: colors.textDim, margin: 0 }}>AI analysis unavailable for this snapshot</p>
      </div>
    );
  }

  return (
    <div style={{ ...cardStyle, borderColor: colors.blue, borderWidth: 1 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h3 style={{ margin: 0 }}>AI Analysis</h3>
          <span style={badgeStyle(colors.blue, colors.blueBg)}>Claude</span>
        </div>
        <span style={{ fontSize: '0.75rem', color: colors.textDim }}>
          {fmtDate(analysis.analyzed_at)}
        </span>
      </div>

      {/* Market Context */}
      {analysis.market_context && (
        <div style={{ ...cardStyle, background: colors.cardAlt, marginBottom: '0.75rem', padding: '0.75rem' }}>
          <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginBottom: '0.25rem', fontWeight: 600 }}>
            Market Context
          </div>
          <p style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.5 }}>{analysis.market_context}</p>
        </div>
      )}

      {/* Risk Warnings */}
      {analysis.risk_warnings && analysis.risk_warnings.length > 0 && (
        <div style={{
          background: colors.amberBg, border: `1px solid ${colors.amber}40`,
          borderRadius: 6, padding: '0.75rem', marginBottom: '0.75rem',
        }}>
          <div style={{ fontSize: '0.75rem', color: colors.amber, fontWeight: 600, marginBottom: '0.35rem' }}>
            Things to Watch
          </div>
          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
            {analysis.risk_warnings.map((w, i) => (
              <li key={i} style={{ fontSize: '0.82rem', color: colors.text, marginBottom: '0.2rem' }}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Summary */}
      {analysis.summary && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginBottom: '0.25rem', fontWeight: 600 }}>
            Analysis Summary
          </div>
          <p style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.6, color: colors.text }}>
            {analysis.summary}
          </p>
        </div>
      )}

      {/* AI Top Picks */}
      {analysis.top_picks && analysis.top_picks.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginBottom: '0.5rem', fontWeight: 600 }}>
            AI Top Picks
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  {['Rank', 'Expiration', 'Strike', 'Credit', 'DTE', 'Reasoning'].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {analysis.top_picks.map((pick, i) => (
                  <tr key={i} style={{ background: i === 0 ? colors.blueBg : 'transparent' }}>
                    <td style={{ ...tdStyle, color: colors.blue, fontWeight: 700 }}>{pick.rank}</td>
                    <td style={tdStyle}>{pick.expiration}</td>
                    <td style={tdStyle}>{pick.strike}</td>
                    <td style={tdStyle}>${pick.credit}</td>
                    <td style={tdStyle}>{pick.dte}</td>
                    <td style={{ ...tdStyle, fontSize: '0.78rem', maxWidth: 350, whiteSpace: 'normal' as const }}>
                      {pick.reasoning}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ranking Confidence — AI vs Our Model */}
      {analysis.ranked_candidates && analysis.ranked_candidates.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginBottom: '0.5rem', fontWeight: 600 }}>
            How AI and Our Model Compare
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  {['AI Position', 'Our Position', 'Agreement', 'Strike', 'Expires', 'Overall Score', 'AI Explanation'].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {analysis.ranked_candidates.map((c, i) => {
                  const diff = Math.abs((c.rank || i + 1) - (c.our_rank || 0));
                  const confidence = diff === 0 ? 'HIGH' : diff <= 2 ? 'MEDIUM' : 'LOW';
                  const confColor = confidence === 'HIGH' ? colors.green : confidence === 'MEDIUM' ? colors.amber : colors.red;
                  return (
                    <tr key={i} style={{ background: confidence === 'HIGH' ? colors.greenBg : 'transparent' }}>
                      <td style={{ ...tdStyle, color: colors.blue, fontWeight: 700 }}>{c.rank || i + 1}</td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{c.our_rank || '—'}</td>
                      <td style={tdStyle}>
                        <span style={badgeStyle(confColor, `${confColor}20`)}>{confidence}</span>
                      </td>
                      <td style={tdStyle}>{c.strike}</td>
                      <td style={tdStyle}>{c.expiration}</td>
                      <td style={tdStyle}>{fmt(c.score_enhanced, 3)}</td>
                      <td style={{ ...tdStyle, fontSize: '0.78rem', maxWidth: 300, whiteSpace: 'normal' as const }}>
                        {c.reasoning}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Download Excel */}
      {analysis.s3_key && (
        <button
          style={{ ...btnStyle, opacity: downloading ? 0.6 : 1 }}
          onClick={onDownload}
          disabled={downloading}
        >
          {downloading ? 'Generating link...' : 'Download Full Excel Report'}
        </button>
      )}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

export const SpxSignals: React.FC = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState<'current' | 'history'>('current');
  const [signals, setSignals] = useState<SpxSignal[]>([]);
  const [selectedSignal, setSelectedSignal] = useState<SpxSignal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  // History state — use ET date, not UTC
  const [historyDate, setHistoryDate] = useState(() => {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  });

  const loadLatest = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const result = await spxService.getLatest(token);
      setSignals(result.signals || []);
      if (result.signals?.length > 0) {
        setSelectedSignal(result.signals[0]);
      } else {
        setSelectedSignal(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load signals');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadByDate = useCallback(async (date: string) => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const result = await spxService.getByDate(token, date);
      setSignals(result.signals || []);
      if (result.signals?.length > 0) {
        setSelectedSignal(result.signals[0]);
      } else {
        setSelectedSignal(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load signals');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === 'current') {
      loadLatest();
    }
  }, [activeTab, loadLatest]);

  const handleDownloadExcel = async () => {
    if (!token || !selectedSignal) return;
    setDownloading(true);
    try {
      const url = await spxService.getExcelUrl(token, selectedSignal.date);
      window.open(url, '_blank');
    } catch (err: any) {
      setError('Failed to generate download link');
    } finally {
      setDownloading(false);
    }
  };

  // Gate check
  if (!canAccessSymbol(user, 'SPX')) {
    return (
      <div className="dashboard-container">
        <Navigation />
        <div style={containerStyle}>
          <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <h2 style={{ marginBottom: '0.75rem' }}>SPX Signals — Pro Feature</h2>
            <p style={{ color: colors.textMuted, marginBottom: '1.5rem', maxWidth: 500, margin: '0 auto 1.5rem' }}>
              SPX put-selling research with AI-powered analysis are available to Pro subscribers.
              Get ranked trade candidates, probability metrics, and Claude AI independent analysis.
            </p>
            <Link to="/upgrade" style={{
              ...btnStyle, textDecoration: 'none', display: 'inline-block',
            }}>
              Upgrade to Pro
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const signal = selectedSignal;
  const bucketOrder = ['0-7', '8-14', '15-30', '31-60'];

  return (
    <div className="dashboard-container">
      <Navigation />
      <div style={containerStyle}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <h1 style={{ margin: 0, fontSize: '1.5rem' }}>SPX Put Selling Signals</h1>
              <span style={badgeStyle(colors.amber, colors.amberBg)}>BETA</span>
            </div>
            {signal && (
              <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginTop: '0.25rem' }}>
                Spot: ${fmt(signal.spot_price)} | Updated: {fmtDate(signal.generated_at)}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ borderBottom: `1px solid ${colors.border}`, marginBottom: '1rem', display: 'flex' }}>
          <button style={tabStyle(activeTab === 'current')} onClick={() => setActiveTab('current')}>Current</button>
          <button style={tabStyle(activeTab === 'history')} onClick={() => setActiveTab('history')}>History</button>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: '#7f1d1d', border: `1px solid ${colors.red}`, borderRadius: 6,
            padding: '0.75rem', marginBottom: '1rem', color: '#fca5a5', fontSize: '0.85rem',
          }}>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '3rem', color: colors.textMuted }}>
            Loading research...
          </div>
        )}

        {/* History date picker */}
        {activeTab === 'history' && !loading && (
          <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.85rem', color: colors.textMuted }}>Date:</label>
            <input
              type="date"
              value={historyDate}
              onChange={(e) => setHistoryDate(e.target.value)}
              style={{
                background: colors.cardAlt, color: colors.text, border: `1px solid ${colors.border}`,
                borderRadius: 4, padding: '0.4rem 0.6rem', fontSize: '0.85rem',
              }}
            />
            <button style={btnStyle} onClick={() => loadByDate(historyDate)}>Load</button>
            {/* Presets */}
            {[
              { label: 'Today', fn: () => { const d = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); setHistoryDate(d); loadByDate(d); }},
              { label: 'Yesterday', fn: () => { const d = new Date(Date.now() - 86400000).toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); setHistoryDate(d); loadByDate(d); }},
            ].map(p => (
              <button key={p.label} onClick={p.fn} style={{
                ...btnStyle, background: colors.border, fontSize: '0.78rem', padding: '0.35rem 0.75rem',
              }}>
                {p.label}
              </button>
            ))}
          </div>
        )}

        {/* Snapshot selector (if multiple snapshots for the day) */}
        {!loading && signals.length > 1 && (
          <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.85rem', color: colors.textMuted }}>Snapshot:</span>
            {signals.map((s) => (
              <button
                key={s.snapshot_time}
                onClick={() => setSelectedSignal(s)}
                style={{
                  ...btnStyle,
                  background: selectedSignal?.snapshot_time === s.snapshot_time ? colors.blue : colors.border,
                  fontSize: '0.78rem', padding: '0.35rem 0.75rem',
                }}
              >
                {fmtDate(s.snapshot_time)}
              </button>
            ))}
          </div>
        )}

        {/* No data */}
        {!loading && !signal && (
          <div style={{ textAlign: 'center', padding: '3rem', color: colors.textMuted }}>
            No research available{activeTab === 'history' ? ' for this date' : ' yet today'}. Readings are generated at 9:30 AM, 12:30 PM, and 3:30 PM ET.
          </div>
        )}

        {/* Signal content */}
        {!loading && signal && (
          <>
            {/* Summary stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
              {[
                { label: 'Spot Price', value: `$${fmt(signal.spot_price)}` },
                { label: 'Capital', value: `$${signal.capital?.toLocaleString() || '90,000'}` },
                { label: 'Options Analyzed', value: String(signal.total_candidates) },
                { label: 'Strategy', value: 'Income from Put Selling' },
              ].map(s => (
                <div key={s.label} style={{
                  ...cardStyle, padding: '0.75rem', textAlign: 'center', marginBottom: 0,
                }}>
                  <div style={{ fontSize: '0.7rem', color: colors.textMuted, marginBottom: '0.25rem' }}>{s.label}</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* IV Skew */}
            {signal.skew && (
              <SkewCard skew={signal.skew} />
            )}

            {/* Best Trade Recommendations */}
            {signal.model_best_trade && (
              <ModelBestTradeCard bestTrade={signal.model_best_trade} />
            )}
            {signal.claude_analysis?.best_trade && (
              <BestTradeCard bestTrade={signal.claude_analysis.best_trade} provider="Claude" providerColor={colors.blue} />
            )}
            {signal.chatgpt_analysis?.best_trade && (
              <BestTradeCard bestTrade={signal.chatgpt_analysis.best_trade} provider="ChatGPT" providerColor="#10b981" />
            )}

            {/* DTE Bucket tables */}
            {bucketOrder.map(key => {
              const bucket = signal.dte_buckets?.[key];
              if (!bucket) return null;
              return <BucketTable key={key} bucket={bucket} bucketKey={key} />;
            })}

            {/* AI Analysis */}
            <AiAnalysisCard
              signal={signal}
              onDownload={handleDownloadExcel}
              downloading={downloading}
            />
          </>
        )}
      </div>
    </div>
  );
};
